import type { ReactNode } from "react";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider } from "@/contexts/auth.context";
import ProtectedRoute from "@/components/auth/ProtectedRoute";
import GuestRoute from "@/components/auth/GuestRoute";
import Layout from "@/components/Layout";
import { FeatureRouteGuard } from "@/components/auth/FeatureRouteGuard";
import LoginPage from "@/components/auth/LoginPage";
import RegisterPage from "@/components/auth/RegisterPage";
import ForgotPasswordPage from "@/components/auth/ForgotPasswordPage";
import ResetPasswordPage from "@/components/auth/ResetPasswordPage";
import AcceptInvitePage from "@/components/auth/AcceptInvitePage";
import TwoFactorPage from "@/components/auth/TwoFactorPage";
import ForceChangePasswordPage from "@/components/auth/ForceChangePasswordPage";
import PlanSelectionPage from "@/components/checkout/PlanSelectionPage";
import CheckoutPage from "@/components/checkout/CheckoutPage";
import TermsPage from "@/pages/TermsPage";
import PrivacyPage from "@/pages/PrivacyPage";
import { 
  FinancialDashboard,
  DailyExpensesPage,
  RevenuesPage,
  MonthlyExpensesPage,
  FutureExpensesPage,
  ReportsPage,
  AgentsPage,
} from "@/pages/financial";
import { HelpCenterPage, TicketsPage } from "@/pages/support";
import { MaterialsPage, VideosPage, BlogPage, VideoPlayerPage, BlogPostPage } from "@/pages/learn";
import { MyAccountPage, BudgetPeriodConfigPage, MentoriaPage } from "@/pages/account";
import NotFound from "./pages/NotFound";
import { ThemeProvider } from "@/components/theme-provider";
import authService from "@/services/auth.service";
import { getForcedTheme } from "@/lib/dark-mode";
import { MaintenanceGate } from "@/components/MaintenanceGate";

const queryClient = new QueryClient();

const AppThemeGate = ({ children }: { children: ReactNode }) => {
  const location = useLocation();
  const forcedTheme = getForcedTheme(location.pathname, authService.isAuthenticated());

  return (
    <ThemeProvider
      attribute="class"
      defaultTheme="light"
      enableSystem={false}
      forcedTheme={forcedTheme}
      disableTransitionOnChange
    >
      {children}
    </ThemeProvider>
  );
};

const AppContent = () => {
  return (
    <MaintenanceGate>
    <AuthProvider>
      <Routes>
        {/* Sessão já válida (Manter Conectado) pula a tela de login */}
        <Route element={<GuestRoute />}>
          <Route path="/login" element={<LoginPage />} />
        </Route>
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/accept-invite" element={<AcceptInvitePage />} />
        <Route path="/two-factor" element={<TwoFactorPage />} />
        <Route path="/change-password" element={<ForceChangePasswordPage />} />
        <Route path="/register/plans" element={<PlanSelectionPage />} />
        <Route path="/register/checkout" element={<CheckoutPage />} />
        <Route path="/termos" element={<TermsPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/privacidade" element={<PrivacyPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/" element={<Navigate to="/financial" replace />} />
        <Route element={<ProtectedRoute />}>
          <Route element={<Layout />}>
            {/* Dashboard Principal - requer dashboard-financeiro */}
            <Route 
              path="/financial" 
              element={
                <FeatureRouteGuard feature="dashboard-financeiro" fallbackPath="/account">
                  <FinancialDashboard />
                </FeatureRouteGuard>
              } 
            />
            <Route 
              path="/dashboard" 
              element={
                <FeatureRouteGuard feature="dashboard-financeiro" fallbackPath="/account">
                  <FinancialDashboard />
                </FeatureRouteGuard>
              } 
            />
            <Route 
              path="/financial/dashboard" 
              element={
                <FeatureRouteGuard feature="dashboard-financeiro" fallbackPath="/account">
                  <FinancialDashboard />
                </FeatureRouteGuard>
              } 
            />
            
            {/* Receitas - requer gestao-de-receitas */}
            <Route 
              path="/financial/revenues" 
              element={
                <FeatureRouteGuard feature="gestao-de-receitas" fallbackPath="/financial">
                  <RevenuesPage />
                </FeatureRouteGuard>
              } 
            />
            
            {/* Despesas Mensais - requer despesas-mensais */}
            <Route 
              path="/financial/monthly-expenses" 
              element={
                <FeatureRouteGuard feature="despesas-mensais" fallbackPath="/financial">
                  <MonthlyExpensesPage />
                </FeatureRouteGuard>
              } 
            />
            
            {/* Despesas do Dia a Dia - requer despesas-do-dia-a-dia */}
            <Route 
              path="/financial/daily-expenses" 
              element={
                <FeatureRouteGuard feature="despesas-do-dia-a-dia" fallbackPath="/financial">
                  <DailyExpensesPage />
                </FeatureRouteGuard>
              } 
            />
            
            {/* Despesas Futuras - requer despesas-futuras */}
            <Route 
              path="/financial/future-expenses" 
              element={
                <FeatureRouteGuard feature="despesas-futuras" fallbackPath="/financial">
                  <FutureExpensesPage />
                </FeatureRouteGuard>
              } 
            />
            
            {/* Relatórios - requer relatorios-financeiros */}
            <Route 
              path="/financial/reports" 
              element={
                <FeatureRouteGuard feature="relatorios-financeiros" fallbackPath="/financial">
                  <ReportsPage />
                </FeatureRouteGuard>
              } 
            />
            
            {/* Agentes - requer agentes */}
            <Route 
              path="/financial/agents" 
              element={
                <FeatureRouteGuard feature="agentes" fallbackPath="/financial">
                  <AgentsPage />
                </FeatureRouteGuard>
              } 
            />
            
            {/* Aprendizado */}
            <Route 
              path="/learn/materials" 
              element={
                <FeatureRouteGuard feature="materiais-de-apoio" fallbackPath="/financial">
                  <MaterialsPage />
                </FeatureRouteGuard>
              } 
            />
            <Route
              path="/learn/videos"
              element={
                <FeatureRouteGuard feature="video-aulas" fallbackPath="/financial">
                  <VideosPage />
                </FeatureRouteGuard>
              }
            />
            <Route
              path="/learn/videos/:id"
              element={
                <FeatureRouteGuard feature="video-aulas" fallbackPath="/financial">
                  <VideoPlayerPage />
                </FeatureRouteGuard>
              }
            />
            <Route
              path="/learn/blog"
              element={
                <FeatureRouteGuard feature="blog" fallbackPath="/financial">
                  <BlogPage />
                </FeatureRouteGuard>
              }
            />
            <Route
              path="/learn/blog/:slug"
              element={
                <FeatureRouteGuard feature="blog" fallbackPath="/financial">
                  <BlogPostPage />
                </FeatureRouteGuard>
              }
            />
            
            {/* Suporte - requer a funcionalidade "suporte" no plano */}
            <Route
              path="/support"
              element={
                <FeatureRouteGuard feature="suporte" fallbackPath="/financial">
                  <TicketsPage />
                </FeatureRouteGuard>
              }
            />
            <Route
              path="/support/tickets"
              element={
                <FeatureRouteGuard feature="suporte" fallbackPath="/financial">
                  <TicketsPage />
                </FeatureRouteGuard>
              }
            />
            {/* Central de Ajuda - sempre acessível (placeholder/ajuda geral) */}
            <Route path="/support/help" element={<HelpCenterPage />} />
            
            {/* Conta - disponível para todos usuários autenticados */}
            <Route path="/account" element={<MyAccountPage />} />
            <Route path="/account/my-account" element={<MyAccountPage />} />
            <Route path="/account/budget-period" element={<BudgetPeriodConfigPage />} />
            <Route
              path="/account/mentoria"
              element={
                <FeatureRouteGuard feature="mentoria-librattum" fallbackPath="/account">
                  <MentoriaPage />
                </FeatureRouteGuard>
              }
            />
          </Route>
        </Route>
        <Route path="*" element={<NotFound />} />
      </Routes>
    </AuthProvider>
    </MaintenanceGate>
  );
};

const App = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AppThemeGate>
          <TooltipProvider>
            <AppContent />
            <Toaster />
          </TooltipProvider>
        </AppThemeGate>
      </BrowserRouter>
    </QueryClientProvider>
  );
};

export default App;
